import React, { useState } from 'react';
import { IoMdCloudDownload } from "react-icons/io";
import { FaAndroid, FaApple, FaGooglePlay, FaQrcode } from "react-icons/fa";
import { HiOutlineDeviceMobile } from "react-icons/hi";
import { BsQrCode } from "react-icons/bs";
import '../../Styles/PublicDownload.css';

const PublicDownloadPage = () => {
  const [downloading, setDownloading] = useState({ 
    user: false, 
    partner: false, 
    executive: false,
    crm: false 
  });
  const [showQR, setShowQR] = useState(false);

  const handleDownload = (type) => {
    setDownloading(prev => ({ ...prev, [type]: true }));

    const apkUrl = type === 'user' 
      ? '/apks/user-app.apk' 
      : type === 'partner'
      ? '/apks/partner-app.apk'
      : type === 'executive'
      ? '/apks/executive-app.apk'
      : '/apks/crm-app.apk';
    
    const fileName = `roam-${type}-app.apk`;
    
    const link = document.createElement('a');
    link.href = apkUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => {
      setDownloading(prev => ({ ...prev, [type]: false }));
    }, 2000);
  };

  const features = [
    {
      icon: '🚀',
      title: 'Fast & Reliable',
      description: 'Experience lightning-fast performance and reliable service'
    },
    {
      icon: '🔒',
      title: 'Secure & Safe',
      description: 'Your data is protected with enterprise-grade security'
    },
    {
      icon: '📱',
      title: 'User Friendly',
      description: 'Intuitive interface designed for everyone'
    },
    {
      icon: '⚡',
      title: 'Real-time Updates',
      description: 'Stay connected with instant notifications'
    }
  ];

  const appTypes = [
    {
      type: 'user',
      title: 'User App',
      description: 'Book rides, track buses, and manage your travel effortlessly',
      icon: '👤',
      color: '#4CAF50',
      version: 'v2.0.1',
      features: ['Book Rides', 'Track Buses', 'Real-time Updates', 'Digital Tickets']
    },
    {
      type: 'partner',
      title: 'Partner App',
      description: 'Manage your fleet, track drivers, and monitor operations in real-time',
      icon: '🏢',
      color: '#2196F3',
      version: 'v2.0.1',
      features: ['Fleet Management', 'Driver Tracking', 'Revenue Analytics', 'Trip Monitoring']
    },
    {
      type: 'executive',
      title: 'Executive App',
      description: 'Manage operations, approve requests, and oversee your entire business',
      icon: '👔',
      color: '#9C27B0',
      version: 'v2.0.1',
      features: ['Approval Management', 'Business Analytics', 'Staff Oversight', 'Performance Reports']
    },
    {
      type: 'crm',
      title: 'CRM App',
      description: 'Manage customer relationships, support tickets, and improve service quality',
      icon: '📊',
      color: '#FF6F00',
      version: 'v2.0.1',
      features: ['Customer Management', 'Support Tickets', 'Feedback Tracking', 'Service Analytics']
    }
  ];

  return (
    <div className="public-download-container">

      <div className="hero-section">
        <div className="hero-content">
          <div className="badge">🚀 Latest Version v2.0.1</div>
          <h1>
            Download <span className="highlight">ROAM</span> App
          </h1>
          <p className="hero-subtitle">
            Your all-in-one transportation solution. Download now and experience 
            seamless travel management on the go.
          </p>

          <div className="download-buttons">
            {appTypes.map((app) => (
              <button
                key={app.type}
                onClick={() => handleDownload(app.type)}
                disabled={downloading[app.type]}
                className={`download-btn ${app.type === 'user' ? 'primary' : app.type === 'partner' ? 'secondary' : app.type === 'executive' ? 'executive' : 'crm'}`}
                style={{
                  backgroundColor: app.color,
                  borderColor: app.color
                }}
              >
                <span className="app-icon">{app.icon}</span>
                <IoMdCloudDownload size={24} />
                {downloading[app.type] ? 'Downloading...' : `Download ${app.title}`}
                <span className="version-tag">{app.version}</span>
              </button>
            ))}
          </div>


          <div className="quick-downloads">
            <span className="quick-label">Quick Download:</span>
            <button 
              className="quick-link user-link"
              onClick={() => handleDownload('user')}
            >
              <FaAndroid /> User
            </button>
            <button 
              className="quick-link partner-link"
              onClick={() => handleDownload('partner')}
            >
              <FaAndroid /> Partner
            </button>
            <button 
              className="quick-link executive-link"
              onClick={() => handleDownload('executive')}
            >
              <FaAndroid /> Executive
            </button>
            <button 
              className="quick-link crm-link"
              onClick={() => handleDownload('crm')}
            >
              <FaAndroid /> CRM
            </button>
          </div>

          <div className="platform-info">
            <div className="platform-item">
              <FaAndroid size={24} />
              <span>Android APK</span>
            </div>
            <div className="platform-divider">|</div>
            <div className="platform-item">
              <HiOutlineDeviceMobile size={24} />
              <span>Version 2.0.1</span>
            </div>
            <div className="platform-divider">|</div>
            <div className="platform-item">
              <span className="file-size">~25 MB</span>
            </div>
          </div>
        </div>

        <div className="hero-image">
          <div className="phone-mockup">
            <div className="phone-screen">
              <div className="app-preview">
                <div className="preview-header">ROAM</div>
                <div className="preview-content">
                  <div className="preview-item"></div>
                  <div className="preview-item"></div>
                  <div className="preview-item"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      <div className="app-types-section">
        <h2>Choose Your App</h2>
        <div className="app-types-grid">
          {appTypes.map((app) => (
            <div key={app.type} className="app-type-card">
              <div className="app-type-icon" style={{ backgroundColor: app.color + '20', color: app.color }}>
                {app.icon}
              </div>
              <h3>{app.title}</h3>
              <p>{app.description}</p>
              <div className="app-features">
                {app.features.map((feature, idx) => (
                  <span key={idx} className="feature-tag">✓ {feature}</span>
                ))}
              </div>
              <button
                onClick={() => handleDownload(app.type)}
                disabled={downloading[app.type]}
                className="app-download-btn"
                style={{ backgroundColor: app.color }}
              >
                <IoMdCloudDownload size={18} />
                {downloading[app.type] ? 'Downloading...' : `Download ${app.title}`}
              </button>
              <span className="app-version">{app.version}</span>
            </div>
          ))}
        </div>
      </div>


      <div className="features-section">
        <h2>Why Choose ROAM?</h2>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>


      <div className="installation-guide">
        <h2>📥 How to Install</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>Download APK</h4>
              <p>Click the download button above for your preferred app version</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>Enable Unknown Sources</h4>
              <p>Go to Settings → Security → Enable "Install from Unknown Sources"</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>Install & Launch</h4>
              <p>Open the downloaded APK and tap "Install". Launch the app and enjoy!</p>
            </div>
          </div>
        </div>
      </div>


      <footer className="public-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <h2>ROAM</h2>
            <p>© 2026 ROAM. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicDownloadPage;